import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import ListSubheader from '@material-ui/core/ListSubheader'
import Typography from './typography'
import Button from 'components/atoms/button'
import { formatDataTestE2eAttr } from 'utilities/format'
import { UNCATEGORIZED_GROUP_KEY } from 'config/consts'

const StyledFormControl = styled(FormControl)`
  width: 100%;

  .MuiFilledInput-root {
    background-color: ${({ theme }) => theme.colors.fieldBackground};
  }
  .MuiFilledInput-root.Mui-error {
    background-color: ${({ theme }) => theme.colors.errorInputBackground};
  }

  ${({ error, required, theme, $value }) =>
    required &&
    !error &&
    [null, ''].includes($value) &&
    `
  .MuiInput-underline:before,
  .MuiInput-underline:after,
  .MuiInput-underline:hover:not(.Mui-disabled):before,
  .MuiFilledInput-underline:before,
  .MuiFilledInput-underline:after,
  .MuiFilledInput-underline:hover:not(.Mui-disabled):before {
    border-color: ${theme.colors.actionsCta};
  }

  .MuiFilledInput-root {
    background-color: ${theme.colors.requiredInputBackground};
  }
  `}
`

const Error = styled(Typography)`
  display: block;
  margin-left: 12px;
  margin-top: ${({ theme }) => theme.sizings.lvl0};
`

const AddNewButtonContainer = styled.div`
  position: sticky;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.sectionBackground};
  border-top: 1px solid ${({ theme }) => theme.colors.brandDelta};
  padding: ${({ theme }) => theme.sizings.lvl1}
    ${({ theme }) => theme.sizings.lvl2};
  margin: 0;
  z-index: 1;
`

/**
 * Generic Categorized Dropdown
 * Allows grouping of options by category (provided in items).
 */
const CategorizedDropdown = ({
  className,
  disabled,
  error,
  filled,
  id,
  items,
  onChange,
  onBlur,
  onActionClick,
  label,
  required,
  value,
  selectionRequired,
  name,
  showActionButton,
  actionButtonText,
  ...restProps
}) => {
  const { t } = useTranslation()

  // Group items by category (if provided)
  const groupedItems = items.reduce((groups, item) => {
    const category = item.category || UNCATEGORIZED_GROUP_KEY
    if (!groups[category]) {
      groups[category] = []
    }
    groups[category].push(item)
    return groups
  }, {})

  const handleChange = (event) => {
    onChange(event.target.value)
  }

  return (
    <StyledFormControl
      $value={value}
      className={className}
      error={!!error}
      required={required}
      variant={filled ? 'filled' : undefined}
      data-test-e2e={formatDataTestE2eAttr(id || name)}
      name={name}
      {...restProps}
    >
      <InputLabel htmlFor={id} disabled={disabled}>
        {label}
      </InputLabel>
      <Select
        inputProps={{
          'data-test-e2e': formatDataTestE2eAttr(id || name, 'input'),
          'aria-labelledby': id,
        }}
        id={id}
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled}
        MenuProps={{
          'data-test-e2e': formatDataTestE2eAttr(id || name, 'menu'),
        }}
      >
        {!selectionRequired && (
          <MenuItem value="">
            --{t('resetSelection', 'Reset selection')}--
          </MenuItem>
        )}

        {Object.entries(groupedItems).length > 0 ? (
          Object.entries(groupedItems).flatMap(([category, categoryItems]) => {
            // MUI Select cannot render fragments, it expects MenuItem or ListSubheader as direct children
            const items = categoryItems.map((item, index) => (
              <MenuItem
                key={`${category}-${item.id || item.value || index}`}
                value={item.value}
                disabled={item.disabled}
              >
                {item.label}
              </MenuItem>
            ))

            if (category !== UNCATEGORIZED_GROUP_KEY) {
              return [
                <ListSubheader key={`header-${category}`} disableSticky>
                  <Typography type="ExplanationParagraph">
                    {t(category)}
                  </Typography>
                </ListSubheader>,
                ...items,
              ]
            }

            return items
          })
        ) : (
          <MenuItem value="" disabled>
            {t('gpsrForm.noOptionsAvailable')}
          </MenuItem>
        )}

        {showActionButton && onActionClick && (
          <AddNewButtonContainer>
            <Button
              type="button"
              level="option"
              icon="add"
              iconSize="sm"
              text={actionButtonText || t('addBrand')}
              onClick={(e) => {
                e.stopPropagation()
                onActionClick()
              }}
              data-test-e2e={formatDataTestE2eAttr(id || name, 'add-button')}
            />
          </AddNewButtonContainer>
        )}
      </Select>

      {typeof error === 'string' && <Error type="ErrorText">{error}</Error>}
    </StyledFormControl>
  )
}

CategorizedDropdown.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  filled: PropTypes.bool,
  id: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
      ]).isRequired,
      disabled: PropTypes.bool,
      category: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onActionClick: PropTypes.func,
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.any,
  selectionRequired: PropTypes.bool,
  name: PropTypes.string,
  showActionButton: PropTypes.bool,
  actionButtonText: PropTypes.string,
}

CategorizedDropdown.defaultProps = {
  className: undefined,
  disabled: false,
  onBlur: undefined,
  onActionClick: undefined,
  error: false,
  id: undefined,
  filled: false,
  required: false,
  selectionRequired: false,
  value: '',
  showActionButton: false,
  actionButtonText: undefined,
}

export default CategorizedDropdown
